/**
 * The trial upsell on the Music24 watermark: the contract of music24-api (`billing.watermarkSummary`,
 * `onboarding.markUpsellSeen`, `GET /accounts/:accountId/clean-copies`) and the rules that the web
 * app uses. The native apps mirror the rules. Spec:
 * `docs/superpowers/specs/2026-10-13-music24-trial-upsell-design.md`. Type imports only.
 */
import type { Music24PlanTier } from './music24Plans';

/** Where an upsell showed. The analytics events and the checkout metadata carry it. */
export const UPSELL_SURFACES = [
  'first_file',
  'download',
  'share',
  'post',
  'zip',
  'viewer',
  'banner',
  'composer',
  'make_dialog',
  'pill',
  'home_card',
  'takeover_last_day',
  'takeover_free',
  'offer',
  'email',
  'push',
] as const;

export type UpsellSurface = (typeof UPSELL_SURFACES)[number];

export function isUpsellSurface(value: unknown): value is UpsellSurface {
  return typeof value === 'string' && (UPSELL_SURFACES as readonly string[]).includes(value);
}

export type WatermarkThumbShape = 'story' | 'square' | 'portrait' | 'wide';

export interface WatermarkThumb {
  /** An absolute picture address (the poster of a video). */
  url: string;
  shape: WatermarkThumbShape;
  isVideo: boolean;
}

/** `billing.watermarkSummary`: the watermarked files of the active workspace. */
export interface WatermarkSummary {
  /** Current, ready kit assets and AI reels with the watermark. */
  files: number;
  /** Draft, scheduled and "needs you" posts that use one of these files. */
  scheduledPosts: number;
  /** Files that the Artist plan cannot make again (full-song videos, motion artwork). */
  needsPro: number;
  /**
   * True while the licence did not get its clean copies yet (one run for each licence). When
   * false, no screen promises clean copies of the files made already.
   */
  cleanCopiesAvailable: boolean;
  byAccount: Array<{ accountId: number; files: number }>;
  /** The newest files, at most `WATERMARK_SUMMARY_THUMBS`. */
  thumbs: WatermarkThumb[];
  /** The title of the song with the most watermarked files. */
  songTitle: string | null;
}

export const WATERMARK_SUMMARY_THUMBS = 4;

export const EMPTY_WATERMARK_SUMMARY: WatermarkSummary = {
  files: 0,
  scheduledPosts: 0,
  needsPro: 0,
  cleanCopiesAvailable: false,
  byAccount: [],
  thumbs: [],
  songTitle: null,
};

/** The automatic modals that open one time for each user (`onboarding.markUpsellSeen`). */
export const UPSELL_SEEN_KINDS = ['firstFile', 'lastDay', 'free'] as const;

export type UpsellSeenKind = (typeof UPSELL_SEEN_KINDS)[number];

/** ISO dates, null = not seen. A field of `onboarding.state` (`upsellSeen`). */
export interface UpsellSeenState {
  firstFile: string | null;
  lastDay: string | null;
  free: string | null;
}

export const NO_UPSELL_SEEN: UpsellSeenState = { firstFile: null, lastDay: null, free: null };

export type CleanCopyItemState = 'clean' | 'making' | 'needsPro' | 'failed';

export interface CleanCopyItem {
  kind: 'kitAsset' | 'reel';
  /** The uuid of the clean copy; for `needsPro`, the uuid of the watermarked file. */
  uuid: string;
  catalogItemUuid: string | null;
  /** English label of the file, for example "Story (Out now)". */
  label: string;
  state: CleanCopyItemState;
  /** 0..100 while the copy is made, else null. */
  progress: number | null;
  width: number | null;
  height: number | null;
}

/** `GET /accounts/:accountId/clean-copies` → `{ cleanCopies: CleanCopiesAnswer | null }`. */
export interface CleanCopiesAnswer {
  status: 'running' | 'done';
  total: number;
  /** Items that are clean. */
  done: number;
  /** Posts that moved, or will move, to a clean file. */
  scheduledPosts: number;
  /** The applied cover of a song has the watermark: the cover editor must export it again. */
  coverHasWatermark: boolean;
  /** The song with the most items, to open after the run. */
  catalogItemUuid: string | null;
  songTitle: string | null;
  items: CleanCopyItem[];
}

/** The Home card needs this many watermarked files. */
export const UPSELL_HOME_CARD_MIN_FILES = 3;
/** The Home card shows from this trial day (day 1 = the signup day). */
export const UPSELL_HOME_CARD_FROM_TRIAL_DAY = 3;
/** "Later" on the Home card hides it for this many hours, on that device. */
export const UPSELL_HOME_CARD_SNOOZE_HOURS = 48;

/** True for the plans whose files carry the watermark. */
export function planHasWatermark(plan: Music24PlanTier): boolean {
  return plan === 'free' || plan === 'trial';
}

export interface UpsellFacts {
  plan: Music24PlanTier;
  /** The user owns the active workspace. A member never sees a price or an automatic modal. */
  isOwner: boolean;
  /** The demo artist is the active artist. */
  isDemo: boolean;
  /** The page runs in the web view of a native app. */
  isNativeEmbed: boolean;
  /** `WatermarkSummary.files`. */
  files: number;
  /** Whole days of trial left; null when the plan is not the trial. */
  trialDaysRemaining: number | null;
  seen: UpsellSeenState;
}

/** True when any watermark surface may show. */
export function upsellApplies(facts: UpsellFacts): boolean {
  return planHasWatermark(facts.plan) && !facts.isDemo && facts.files > 0;
}

/**
 * The automatic modal that may open now, or null. The caller opens at most one in a session, and
 * never on top of another modal, in the tour or on the welcome page.
 */
export function upsellAutoModal(facts: UpsellFacts): UpsellSeenKind | null {
  if (!upsellApplies(facts) || !facts.isOwner || facts.isNativeEmbed) {
    return null;
  }
  if (facts.plan === 'free') {
    return facts.seen.free === null ? 'free' : null;
  }
  if (facts.trialDaysRemaining !== null && facts.trialDaysRemaining <= 1) {
    return facts.seen.lastDay === null ? 'lastDay' : null;
  }
  return facts.seen.firstFile === null ? 'firstFile' : null;
}

/** True when the Home card may show (before the device snooze). */
export function upsellShowsHomeCard(facts: UpsellFacts, trialDays: number): boolean {
  if (!upsellApplies(facts) || !facts.isOwner || facts.files < UPSELL_HOME_CARD_MIN_FILES) {
    return false;
  }
  if (facts.plan === 'free' || facts.trialDaysRemaining === null) {
    return true;
  }
  const trialDay = trialDays - facts.trialDaysRemaining + 1;
  return trialDay >= UPSELL_HOME_CARD_FROM_TRIAL_DAY;
}

/** The plan that the offer selects: Pro when a file needs it, else Artist. */
export function offerSelectedTier(summary: Pick<WatermarkSummary, 'needsPro'>): 'artist' | 'pro' {
  return summary.needsPro > 0 ? 'pro' : 'artist';
}
